import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  updateProfile, 
  sendPasswordResetEmail,
  updateCurrentUser
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  serverTimestamp,
  Timestamp,
  writeBatch,
  addDoc,
  orderBy
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { connectFirestoreEmulator, enableIndexedDbPersistence } from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  console.log('AuthProvider: Initializing...');
  
  // Debug Firebase connection
  useEffect(() => {
    console.log('Firebase Auth:', auth ? 'Connected' : 'Not connected');
    console.log('Firestore DB:', db ? 'Connected' : 'Not connected');

    // Enable offline persistence
    const enablePersistence = async () => {
      try {
        await enableIndexedDbPersistence(db);
        console.log('Firestore offline persistence enabled');
      } catch (err) {
        if (err.code === 'failed-precondition') {
          console.warn('Offline persistence can only be enabled in one tab at a time.');
        } else if (err.code === 'unimplemented') {
          console.warn('The current browser does not support offline persistence.');
        } else {
          console.error('Error enabling offline persistence:', err);
        }
      }
    };

    enablePersistence();
  }, []);
  
  // Authentication state
  const [currentUser, setCurrentUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Load initial data from localStorage
  const [patients, setPatients] = useState(() => {
    const saved = localStorage.getItem('patients');
    return saved ? JSON.parse(saved) : [];
  });

  const [surveyTemplates, setSurveyTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [surveyAssignments, setSurveyAssignments] = useState([]);

  // Load survey templates from Firestore on mount
  useEffect(() => {
    const loadSurveyTemplates = async () => {
      try {
        setLoadingTemplates(true);
        const templatesSnapshot = await getDocs(collection(db, 'surveyTemplates'));
        const templates = [];
        
        templatesSnapshot.forEach((doc) => {
          templates.push({ id: doc.id, ...doc.data() });
        });

        // If no templates in Firestore, initialize with default templates
        if (templates.length === 0) {
          const defaultTemplates = [
            {
              id: '1',
              name: 'Follow-up Care Assessment',
              description: 'Assess patient recovery and follow-up care needs',
              category: 'Follow-up Care',
              estimatedTime: '5-10 minutes',
              questions: [
                {
                  id: '1',
                  type: 'multiple-choice',
                  question: 'How would you rate your recovery progress?',
                  options: ['Excellent', 'Good', 'Fair', 'Poor'],
                  required: true
                },
                {
                  id: '2',
                  type: 'yes-no',
                  question: 'Have you experienced any unexpected symptoms?',
                  required: true
                },
                {
                  id: '3',
                  type: 'text',
                  question: 'Please describe any concerns or questions you have:',
                  required: false
                }
              ],
              createdAt: '2023-01-01T00:00:00.000Z',
              updatedAt: '2023-01-01T00:00:00.000Z'
            },
            {
              id: '2',
              name: 'Patient Satisfaction Survey',
              description: 'Gather feedback about the patient experience',
              category: 'Patient Satisfaction',
              estimatedTime: '3-5 minutes',
              questions: [
                {
                  id: '1',
                  type: 'rating',
                  question: 'How would you rate your overall experience?',
                  required: true
                },
                {
                  id: '2',
                  type: 'multiple-choice',
                  question: 'How likely are you to recommend our practice to others?',
                  options: ['Very Likely', 'Likely', 'Neutral', 'Unlikely', 'Very Unlikely'],
                  required: true
                },
                {
                  id: '3',
                  type: 'textarea',
                  question: 'Do you have any additional comments or suggestions?',
                  required: false
                }
              ],
              createdAt: '2023-01-01T00:00:00.000Z',
              updatedAt: '2023-01-01T00:00:00.000Z'
            }
          ];

          // Save default templates to Firestore
          const batch = writeBatch(db);
          defaultTemplates.forEach(template => {
            const templateRef = doc(collection(db, 'surveyTemplates'), template.id);
            batch.set(templateRef, template);
          });
          await batch.commit();
          
          setSurveyTemplates(defaultTemplates);
        } else {
          setSurveyTemplates(templates);
        }
      } catch (error) {
        console.error('Error loading survey templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };

    loadSurveyTemplates();
  }, []);

  useEffect(() => {
    console.log('🔑 Auth state changed - Initial check');
    
    // Check if user is already logged in
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔄 Auth state changed - New state:', user ? 'User logged in' : 'No user');
      
      if (user) {
        console.log('👤 User authenticated:', { uid: user.uid, email: user.email });
        
        try {
          console.log('📡 Fetching user document from Firestore...');
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log('✅ User document found:', { uid: user.uid, role: userData.role });
            
            // Update state with user data
            setCurrentUser({ 
              uid: user.uid, 
              email: user.email,
              ...userData 
            });
            setUserType(userData.role);
            
            // If user is a clinician, fetch their patients and then their survey assignments
            if (userData.role === 'clinician') {
              console.log('🏥 User is a clinician, fetching patients and assignments...');
              await fetchPatients();
              // Fetch all survey assignments for the clinician's patients
              await fetchSurveyAssignments(user.uid, true);
            } else {
              console.log('👤 User is a patient, fetching their survey assignments');
              // Fetch only this patient's survey assignments
              await fetchSurveyAssignments(user.uid, false);
            }
          } else {
            console.error('❌ User document not found in Firestore');
            await signOut(auth);
          }
        } catch (error) {
          console.error('❌ Error in auth state change:', error);
          setError('Failed to load user data');
          await signOut(auth);
        }
      } else {
        // User is signed out
        setCurrentUser(null);
        setUserType(null);
        setPatients([]);
      }
      
      setLoading(false);
    });
    
    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      console.log('🔐 Attempting login with:', email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      console.log('✅ Login successful, fetching user data...');
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }
      
      const userData = userDoc.data();
      
      // Update state with user data and role
      const currentUserData = {
        uid: user.uid,
        email: user.email,
        ...userData
      };
      
      setCurrentUser(currentUserData);
      setUserType(userData.role);
      
      // Fetch relevant data based on user role
      if (userData.role === 'clinician') {
        await fetchPatients();
      } else if (userData.role === 'patient') {
        // For patients, fetch their survey assignments
        await fetchSurveyAssignments(user.uid);
      }
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = 'Failed to log in. Please check your credentials.';
      
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMessage = 'Invalid email or password';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      setUserType(null);
      setSurveyAssignments([]); // Clear survey assignments on logout
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Function to fetch survey assignments for a patient or all patients of a clinician
  const fetchSurveyAssignments = async (userId, isClinician = false) => {
    try {
      console.log(`🔍 Fetching survey assignments for ${isClinician ? 'clinician' : 'patient'}:`, userId);
      
      let q;
      
      if (isClinician) {
        // For clinicians, get all assignments for their patients
        const patientIds = patients.map(p => p.id);
        
        if (patientIds.length === 0) {
          console.log('No patients found for clinician, no assignments to fetch');
          setSurveyAssignments([]);
          return [];
        }
        
        q = query(
          collection(db, 'surveyAssignments'),
          where('patientId', 'in', patientIds)
        );
      } else {
        // For patients, just get their own assignments
        q = query(
          collection(db, 'surveyAssignments'),
          where('patientId', '==', userId)
        );
      }
      
      const querySnapshot = await getDocs(q);
      const assignments = [];
      
      querySnapshot.forEach((doc) => {
        assignments.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log(`📋 Found ${assignments.length} survey assignments`);
      
      // For clinicians, we want to keep all assignments in state
      // For patients, we only keep their own assignments
      const updatedAssignments = isClinician 
        ? assignments 
        : assignments.filter(a => a.patientId === userId);
      
      setSurveyAssignments(updatedAssignments);
      return assignments;
      
    } catch (error) {
      console.error('Error fetching survey assignments:', {
        error: error.toString(),
        errorCode: error.code,
        errorMessage: error.message,
        userId,
        isClinician,
        timestamp: new Date().toISOString()
      });
      
      // On error, return empty array for now
      setSurveyAssignments([]);
      return [];
    }
  };

  // Memoize fetchPatients to prevent infinite loops
  const fetchPatients = React.useCallback(async () => {
    console.log('=== FETCH_PATIENTS STARTED ===');
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      console.log('❌ No current user, cannot fetch patients');
      setPatients([]);
      return [];
    }
    
    console.log('👤 Current user ID:', currentUser.uid);
    
    try {
      // Debug: Check if Firestore is properly initialized
      console.log('🔍 Database instance:', db ? '✅ Initialized' : '❌ Not initialized');
      
      console.log('1. 🔍 Getting current user document...');
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      
      if (!userDoc.exists()) {
        const errorMsg = '❌ Current user document not found in Firestore';
        console.error(errorMsg);
        setError(errorMsg);
        return [];
      }
      
      const userData = userDoc.data();
      console.log('2. 📋 Current user data:', {
        ...userData,
        hasRole: 'role' in userData,
        role: userData.role,
        isClinician: userData.role === 'clinician'
      });
      
      if (userData.role !== 'clinician') {
        const errorMsg = '⚠️ Current user is not a clinician';
        console.log(errorMsg);
        setError(errorMsg);
        return [];
      }
      
      console.log('3. 🔍 Preparing Firestore query for patients...');
      console.log('   - Collection: users');
      console.log('   - Filters: role == "patient", (createdBy ==', currentUser.uid, 'OR createdBy == "self_registered")');
      
      // Get the users collection reference
      const usersCollection = collection(db, 'users');
      
      // Create a query that gets both the clinician's patients and self-registered patients
      // We need to use the query constraints correctly for OR conditions
      const q = query(
        usersCollection,
        where('role', '==', 'patient'),
        where('createdBy', 'in', [currentUser.uid, 'self_registered'])
      );
      
      console.log('4. ⏳ Executing patient query...');
      const querySnapshot = await getDocs(q);
      console.log('5. ✅ Query completed. Found', querySnapshot.size, 'patients');
      
      let patientsList = [];
      
      if (querySnapshot.empty) {
        console.log('ℹ️ No patients found for this clinician');
        console.log('   - Current user ID:', currentUser.uid);
        console.log('   - Query filters: role=patient, createdBy=' + currentUser.uid);
        
        setPatients([]);
        return [];
      }
      
      console.log('6. Processing patient documents...');
      querySnapshot.forEach((doc) => {
        try {
          const data = doc.data();
          console.log('   - Processing document ID:', doc.id);
          console.log('     Raw document data:', data);
          
          const patientData = { 
            id: doc.id, 
            ...data,
            // Ensure we have a display name
            name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unnamed Patient',
            // Add firstName and lastName if they don't exist (for backward compatibility)
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            // Ensure groups array exists (default to empty array if not set)
            groups: Array.isArray(data.groups) ? data.groups : [],
            createdAt: data.createdAt || new Date().toISOString()
          };
          
          console.log('     Patient groups:', patientData.groups);
          
          console.log('     Processed patient data:', patientData);
          patientsList.push(patientData);
        } catch (docError) {
          console.error('❌ Error processing document:', {
            documentId: doc.id,
            error: docError,
            errorMessage: docError.message,
            stack: docError.stack
          });
        }
      });
      
      // Sort patients by last name, then first name
      patientsList = patientsList.sort((a, b) => {
        const nameA = `${a.lastName} ${a.firstName}`.toUpperCase();
        const nameB = `${b.lastName} ${b.firstName}`.toUpperCase();
        return nameA.localeCompare(nameB);
      });
      
      console.log('7. Completed processing. Total patients found:', patientsList.length);
      console.log('   Patient IDs:', patientsList.map(p => p.id));
      console.log('   Patient names:', patientsList.map(p => `${p.firstName} ${p.lastName}`));
      
      // Update local state with the fetched patients
      setPatients(patientsList);
      console.log('✅ Patients state updated with', patientsList.length, 'patients');
      
      // If this is a clinician, fetch survey assignments for all patients
      if (currentUser && userType === 'clinician' && patientsList.length > 0) {
        console.log('🔄 Fetching survey assignments for all patients...');
        await fetchSurveyAssignments(currentUser.uid, true);
      } else if (patientsList.length === 0) {
        console.log('⚠️ No patients found for this clinician');
        console.log('   - Query filters used:');
        console.log('     - collection: users');
        console.log('     - where: role == "patient"');
        console.log('     - where: createdBy ==', currentUser?.uid);
        console.log('   - Possible issues:');
        console.log('     - No patients exist in the database');
        console.log('     - Patients exist but were created by a different user');
        console.log('     - The "role" or "createdBy" fields are missing or incorrect');
        setSurveyAssignments([]);
      }
      
      console.log('=== FETCH_PATIENTS COMPLETED ===');
      return patientsList;
      
    } catch (error) {
      const errorMsg = `Error in fetchPatients: ${error.message}`;
      console.error(errorMsg, error);
      setError(errorMsg);
      setPatients([]);
      return [];
    }
  }, [auth, db, setPatients, setError]); // Add all dependencies used in the callback

  // Register a new patient (self-registration)
  const registerPatient = async (patientData) => {
    const auth = getAuth();
    const { email, password, ...patientInfo } = patientData;
    
    try {
      console.log('Starting patient self-registration:', email);
      
      // 1. Create the new user account
      console.log('Creating auth user...');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const newUser = userCredential.user;
      
      // 2. Create the user document in Firestore
      const userData = {
        ...patientInfo,
        email: email.toLowerCase().trim(),
        role: 'patient',
        // For self-registered patients, we'll use a special 'self_registered' value for createdBy
        // This will help identify them in queries
        createdBy: 'self_registered',
        // Add firstName and lastName for consistency
        firstName: patientInfo.firstName || '',
        lastName: patientInfo.lastName || '',
        // Ensure we have a proper name field
        name: `${patientInfo.firstName || ''} ${patientInfo.lastName || ''}`.trim() || 'New Patient',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        // Add any additional fields that might be expected
        phone: patientInfo.phone || '',
        dateOfBirth: patientInfo.dateOfBirth || null,
        gender: patientInfo.gender || 'unspecified',
        // Mark as active by default
        status: 'active'
      };
      
      console.log('Saving patient data to Firestore...', userData);
      await setDoc(doc(db, 'users', newUser.uid), userData);
      
      // 3. Sign in the new user
      await signInWithEmailAndPassword(auth, email, password);
      
      return { 
        success: true, 
        user: { 
          uid: newUser.uid, 
          ...userData
        } 
      };
    } catch (error) {
      console.error('Patient registration error:', error);
      let errorMessage = 'Failed to register. Please try again.';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this email already exists. Please sign in instead.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  // Add a new patient (for clinicians)
  const addPatient = async (patientData) => {
    const auth = getAuth();
    const { email, password, ...patientInfo } = patientData;
    
    try {
      console.log('Starting to add patient:', email);
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }
      
      // 1. Store the current user's session
      const currentUserData = {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName,
        photoURL: currentUser.photoURL,
        // Add any other necessary user properties
      };
      
      // 2. Create the new user account
      console.log('Creating auth user...');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const newUser = userCredential.user;
      
      // 3. Create the user document in Firestore
      const userData = {
        ...patientInfo,
        email,
        role: 'patient',
        createdBy: currentUser.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        name: `${patientInfo.firstName || ''} ${patientInfo.lastName || ''}`.trim(),
        firstName: patientInfo.firstName || '',
        lastName: patientInfo.lastName || ''
      };
      
      console.log('Saving user data to Firestore...', userData);
      await setDoc(doc(db, 'users', newUser.uid), userData);
      
      // 4. Immediately restore the original user session
      console.log('Restoring original user session...');
      await updateCurrentUser(auth, currentUser);
      
      // 5. Fetch the updated patient list
      console.log('Fetching updated patient list...');
      await fetchPatients();
      
      // Return the new patient data including the ID for redirection
      return { 
        success: true, 
        patient: { 
          id: newUser.uid, 
          ...userData,
          uid: newUser.uid
        } 
      };
    } catch (error) {
      console.error('Error adding patient:', error);
      let errorMessage = 'Failed to add patient';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'A patient with this email already exists';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const updateUserProfile = async (profileData) => {
    if (!currentUser) return { success: false, error: 'No user is logged in' };
    
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        ...profileData,
        updatedAt: new Date().toISOString()
      });
      
      // Update local state
      const updatedUser = {
        ...currentUser,
        ...profileData
      };
      setCurrentUser(updatedUser);
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      return { success: true, user: updatedUser };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, error: 'Failed to update profile' };
    }
  };

  // Update an existing patient
  const updatePatient = async (patientId, updatedData) => {
    try {
      if (!patientId) {
        throw new Error('Patient ID is required');
      }
      
      console.log('Updating patient in Firestore:', { patientId, updatedData });
      
      // Ensure groups and conditions are always arrays and remove duplicates
      const sanitizedData = {
        ...updatedData,
        groups: Array.isArray(updatedData.groups) 
          ? [...new Set(updatedData.groups.filter(Boolean))] // Remove duplicates and empty values
          : [],
        conditions: Array.isArray(updatedData.conditions)
          ? [...new Set(updatedData.conditions.filter(Boolean))] // Remove duplicates and empty values
          : [],
        updatedAt: new Date().toISOString()
      };
      
      const patientRef = doc(db, 'users', patientId);
      await updateDoc(patientRef, sanitizedData);
      
      // Update local state with the sanitized data
      setPatients(prev => {
        const updated = prev.map(p => 
          p.id === patientId 
            ? { 
                ...p, 
                ...sanitizedData,
                // Preserve the ID and other important fields
                id: p.id,
                uid: p.uid || p.id
              } 
            : p
        );
        console.log('Updated local patients state:', updated);
        return updated;
      });
      
      return { 
        success: true,
        patient: {
          id: patientId,
          ...sanitizedData
        }
      };
    } catch (error) {
      console.error('Error updating patient:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to update patient' 
      };
    }
  };
  
  // Delete a patient
  const deletePatient = async (patientId) => {
    try {
      if (!patientId) {
        throw new Error('Patient ID is required');
      }
      
      // Note: In a real app, you might want to mark the patient as inactive
      // instead of deleting them to preserve data integrity
      await deleteDoc(doc(db, 'users', patientId));
      
      // Update local state
      setPatients(prev => prev.filter(p => p.id !== patientId));
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting patient:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to delete patient' 
      };
    }
  };

  // Helper function to calculate next due date based on frequency
  const calculateNextDueDate = (dueDate, frequency) => {
    if (!frequency || frequency === 'none') return null;
    
    const date = new Date(dueDate);
    switch (frequency) {
      case 'weekly':
        date.setDate(date.getDate() + 7);
        break;
      case 'biweekly':
        date.setDate(date.getDate() + 14);
        break;
      case 'monthly':
        date.setMonth(date.getMonth() + 1);
        break;
      default:
        return null;
    }
    return date.toISOString().split('T')[0];
  };

  const assignSurveyToPatient = async (surveyAssignment) => {
    try {
      console.log('Starting survey assignment with data:', JSON.stringify(surveyAssignment, null, 2));
      
      // Prepare the base assignment
      const baseAssignment = {
        ...surveyAssignment,
        id: surveyAssignment.id || `assignment-${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isRecurring: !!surveyAssignment.repeat && surveyAssignment.repeat !== 'none',
        repeatFrequency: surveyAssignment.repeat || 'none',
        repeatEndDate: surveyAssignment.repeatEndDate || null,
        parentAssignmentId: surveyAssignment.parentAssignmentId || null,
        sequenceNumber: surveyAssignment.sequenceNumber || 1
      };

      // If this is a recurring survey, calculate the next due date
      const nextDueDate = calculateNextDueDate(surveyAssignment.dueDate, surveyAssignment.repeat);
      if (nextDueDate && surveyAssignment.repeat !== 'none') {
        baseAssignment.nextDueDate = nextDueDate;
      }

      console.log('Prepared assignment data:', JSON.stringify(baseAssignment, null, 2));
      
      try {
        // Save to Firestore
        console.log('Attempting to save to Firestore collection: surveyAssignments');
        const assignmentRef = await addDoc(collection(db, 'surveyAssignments'), baseAssignment);
        console.log('Successfully saved to Firestore with ID:', assignmentRef.id);
        
        // Update local state
        const updatedAssignment = { ...baseAssignment, id: assignmentRef.id };
        
        // If this is a new recurring assignment, create the next one if needed
        if (nextDueDate && !surveyAssignment.parentAssignmentId && surveyAssignment.repeat !== 'none') {
          const nextAssignment = {
            ...updatedAssignment,
            id: `assignment-${Date.now() + 1}`, // Temporary ID, will be replaced by Firestore
            parentAssignmentId: assignmentRef.id,
            dueDate: nextDueDate,
            sequenceNumber: 2, // This is the next in sequence
            status: 'pending', // Next assignments start as pending
            assignedDate: new Date().toISOString(),
            responses: null
          };
          
          // Recursively create the next assignment
          await assignSurveyToPatient(nextAssignment);
        }
        
        setSurveyAssignments(prev => [...prev, updatedAssignment]);
        return updatedAssignment;
      } catch (firestoreError) {
        console.error('Firestore error details:', {
          name: firestoreError.name,
          message: firestoreError.message,
          code: firestoreError.code,
          stack: firestoreError.stack
        });
        throw firestoreError;
      }
    } catch (error) {
      console.error('Error in assignSurveyToPatient:', {
        name: error.name,
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw new Error(`Failed to assign survey: ${error.message}`);
    }
  };

  const getSurveyAssignmentsForPatient = async (patientId) => {
    try {
      // If we already have assignments in state, return them immediately
      const localAssignments = surveyAssignments.filter(a => a.patientId === patientId);
      if (localAssignments.length > 0) {
        return localAssignments;
      }

      // Otherwise, fetch from Firestore
      const assignmentsQuery = query(
        collection(db, 'surveyAssignments'),
        where('patientId', '==', patientId),
        orderBy('assignedDate', 'desc')
      );
      
      const querySnapshot = await getDocs(assignmentsQuery);
      const fetchedAssignments = [];
      
      querySnapshot.forEach((doc) => {
        fetchedAssignments.push({ id: doc.id, ...doc.data() });
      });
      
      // Update local state with fetched assignments
      const newAssignments = [...surveyAssignments];
      fetchedAssignments.forEach(fa => {
        if (!newAssignments.some(a => a.id === fa.id)) {
          newAssignments.push(fa);
        }
      });
      
      setSurveyAssignments(newAssignments);
      return fetchedAssignments;
      
    } catch (error) {
      console.error('Error fetching survey assignments:', error);
      // Fall back to local state if there's an error
      return surveyAssignments.filter(assignment => assignment.patientId === patientId);
    }
  };

  const updateSurveyAssignment = async (updatedAssignment) => {
    console.log('Starting updateSurveyAssignment with:', {
      id: updatedAssignment.id,
      hasResponses: !!updatedAssignment.responses,
      status: updatedAssignment.status,
      patientId: updatedAssignment.patientId,
      surveyId: updatedAssignment.surveyId
    });

    try {
      // Get the survey template to include the survey name
      const surveyTemplate = surveyTemplates.find(t => t.id === updatedAssignment.surveyId);
      console.log('Found survey template:', {
        templateId: surveyTemplate?.id,
        templateTitle: surveyTemplate?.title
      });
      
      // Ensure we have a valid ID
      if (!updatedAssignment.id) {
        throw new Error('Cannot update survey assignment: Missing assignment ID');
      }

      // Prepare the update data
      const updateData = {
        ...updatedAssignment,
        updatedAt: new Date().toISOString(),
        surveyTitle: surveyTemplate?.title || updatedAssignment.surveyTitle || 'Untitled Survey',
        // Ensure these fields are always included
        patientId: updatedAssignment.patientId || currentUser?.uid,
        surveyId: updatedAssignment.surveyId,
        status: updatedAssignment.status || 'completed',
        assignedDate: updatedAssignment.assignedDate || new Date().toISOString(),
        dueDate: updatedAssignment.dueDate || null,
        completedDate: updatedAssignment.completedDate || (updatedAssignment.status === 'completed' ? new Date().toISOString() : null),
        responses: updatedAssignment.responses || {}
      };
      
      console.log('Prepared update data:', JSON.stringify(updateData, null, 2));
      
      // Update in Firestore
      console.log('Updating Firestore document...');
      const assignmentRef = doc(db, 'surveyAssignments', updatedAssignment.id);
      console.log('Document reference created:', assignmentRef.path);
      
      // Use setDoc with merge: true to handle partial updates
      await setDoc(assignmentRef, updateData, { merge: true });
      console.log('Firestore update successful');
      
      // Update local state
      const updatedAssignments = surveyAssignments.map(assignment => 
        assignment.id === updatedAssignment.id 
          ? { ...updateData } 
          : assignment
      );
      
      setSurveyAssignments(updatedAssignments);
      console.log('Local state updated successfully');
      
      return { ...updateData };
      
    } catch (error) {
      console.error('❌ Error in updateSurveyAssignment:', {
        error: error.toString(),
        errorCode: error.code,
        errorMessage: error.message,
        errorStack: error.stack,
        assignmentId: updatedAssignment.id,
        currentUser: currentUser?.uid,
        timestamp: new Date().toISOString()
      });
      
      // More detailed error handling
      if (error.code === 'permission-denied') {
        console.error('❌ Firestore permission denied. Check your security rules.');
      } else if (error.code === 'not-found') {
        console.error('❌ Document not found. It may have been deleted.');
      }
      
      // Re-throw with a more descriptive message
      throw new Error(`Failed to update survey assignment: ${error.message}`);
    }
  };

  const saveSurveyTemplate = async (template) => {
    try {
      const templateRef = doc(db, 'surveyTemplates', template.id);
      await setDoc(templateRef, template, { merge: true });
      
      // Update local state after successful Firestore save
      setSurveyTemplates(prev => {
        const existing = prev.find(t => t.id === template.id);
        if (existing) {
          return prev.map(t => t.id === template.id ? template : t);
        } else {
          return [...prev, template];
        }
      });
      
      console.log('Survey template saved successfully:', template.id);
    } catch (error) {
      console.error('Error saving survey template:', error);
      throw error; // Re-throw to allow handling in the component
    }
  };

  const deleteSurveyTemplate = async (templateId) => {
    try {
      // Remove from Firestore
      await deleteDoc(doc(db, 'surveyTemplates', templateId));
      
      // Update local state
      setSurveyTemplates(prev => prev.filter(t => t.id !== templateId));
      
      console.log('Survey template deleted successfully:', templateId);
    } catch (error) {
      console.error('Error deleting survey template:', error);
      throw error; // Re-throw to allow handling in the component
    }
  };

  const getSurveyTemplate = (templateId) => {
    return surveyTemplates.find(t => t.id === templateId);
  };

  // Function to create a test clinician account (for development only)
  const createTestClinician = async () => {
    try {
      // Create auth user
      const email = 'dr.smith@hospital.com';
      const password = 'password123';
      
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Create user document in Firestore
      const userData = {
        email,
        firstName: 'Sarah',
        lastName: 'Smith',
        role: 'clinician',
        title: 'Dr.',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      await setDoc(doc(db, 'users', user.uid), userData);
      
      return { success: true, user: { uid: user.uid, ...userData } };
    } catch (error) {
      console.error('Error creating test clinician:', error);
      return { 
        success: false, 
        error: error.code === 'auth/email-already-in-use' 
          ? 'Test clinician already exists' 
          : 'Failed to create test clinician' 
      };
    }
  };

  // Clear error message
  const clearError = () => setError('');

  // Stub functions for survey functionality
  const submitSurveyResponse = async () => ({});
  const getSurveyResponsesForPatient = () => [];
  const getSurveyResponse = () => null;
  const getPatientById = (id) => patients.find(p => p.id === id) || null;
  const getSurveyTemplateById = (id) => surveyTemplates.find(t => t.id === id) || null;

  const value = {
    // Auth state
    currentUser,
    userType,
    loading,
    error,
    
    // Auth methods
    login,
    logout,
    registerPatient,
    clearError,
    
    // Test account (development only)
    createTestClinician,
    
    // Patient management
    addPatient,
    updatePatient,
    deletePatient,
    fetchPatients,
    setPatients,
    patients,
    
    // User profile
    updateUserProfile,
    
    // Survey functionality
    getSurveyTemplate,
    surveyTemplates: surveyTemplates || [],
    assignSurveyToPatient,
    surveyAssignments: surveyAssignments || [],
    getSurveyAssignmentsForPatient,
    updateSurveyAssignment,
    saveSurveyTemplate,
    deleteSurveyTemplate,
    submitSurveyResponse,
    getSurveyResponsesForPatient,
    getSurveyResponse,
    getPatientById,
    getSurveyTemplateById,
    fetchSurveyAssignments
  };

  // Debug effect to log state changes
  useEffect(() => {
    console.log('AuthProvider: State updated', {
      currentUser: currentUser ? { 
        uid: currentUser.uid, 
        email: currentUser.email,
        emailVerified: currentUser.emailVerified
      } : null,
      userType,
      loading,
      error,
      patientsCount: patients?.length || 0,
      surveyAssignmentsCount: surveyAssignments?.length || 0,
      surveyTemplatesCount: surveyTemplates?.length || 0
    });
  }, [currentUser, userType, loading, error, patients, surveyAssignments, surveyTemplates]);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
